import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Volume2,
  Users,
  Languages,
  Bot,
  BookPlus,
  ArrowLeft,
  Loader2,
  Mail,
  Bell,
} from "lucide-react";
import { toast } from "sonner";
import { addToDictionary, detectLanguage, generateAcronym } from "@/lib/aacDictionary";
import { supabase } from "@/integrations/supabase/client";
import { speak } from "@/lib/ttsService";
import { useAuth } from "@/hooks/useAuth";
import { MainLayout } from "@/components/layout/MainLayout";
import { useTranslation } from "@/hooks/useTranslation";

type View = "main" | "translate" | "sendChoice";

interface ContactConfig {
  hasEmail: boolean;
  hasPush: boolean;
  email?: string;
}

const targetLanguages = [
  { code: "en", name: "English", nameHe: "אנגלית" },
  { code: "he", name: "עברית", nameHe: "עברית" },
  { code: "ru", name: "Русский", nameHe: "רוסית" },
  { code: "ar", name: "العربية", nameHe: "ערבית" },
  { code: "es", name: "Español", nameHe: "ספרדית" },
];

export default function AACActions() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t, lang, dir } = useTranslation();
  
  // Get text from query params or sessionStorage
  const params = new URLSearchParams(location.search);
  const textFromUrl = params.get("text") || "";
  const text = textFromUrl || sessionStorage.getItem("aacActionText") || "";
  const returnPath = params.get("return") || sessionStorage.getItem("aacActionReturn") || "/";
  
  const [view, setView] = useState<View>("main");
  const [translatedText, setTranslatedText] = useState("");
  const [targetLang, setTargetLang] = useState("");
  const [isTranslating, setIsTranslating] = useState(false);
  const [contactConfig, setContactConfig] = useState<ContactConfig>({ hasEmail: false, hasPush: false });
  const [isSendingPush, setIsSendingPush] = useState(false);

  const isUIHebrew = lang === "he" || lang === "ar";
  const isHebrew = detectLanguage(text) === "he";
  
  // Fetch contact configuration on mount
  useEffect(() => {
    const fetchContactConfig = async () => {
      const caregiverData = localStorage.getItem("ayuni-caregiver");
      const caregiver = caregiverData ? JSON.parse(caregiverData) : null;
      const hasEmail = !!caregiver?.email;
      
      let hasPush = false;
      if (user) {
        const { data } = await supabase
          .from('user_settings')
          .select('caregiver_push_tokens')
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (data?.caregiver_push_tokens && Array.isArray(data.caregiver_push_tokens)) {
          hasPush = data.caregiver_push_tokens.length > 0;
        }
      }
      
      setContactConfig({ hasEmail, hasPush, email: caregiver?.email });
    };
    
    fetchContactConfig();
  }, [user]);
  
  const handleBack = () => {
    sessionStorage.removeItem("aacActionText");
    sessionStorage.removeItem("aacActionReturn");
    navigate(returnPath);
  };

  const handleClearAndNavigate = () => {
    sessionStorage.removeItem("aacActionText");
    sessionStorage.removeItem("aacActionReturn");
  };

  const handleSendEmail = (messageText: string = text) => {
    const email = contactConfig.email;
    if (!email) return;
    
    const subject = encodeURIComponent(isUIHebrew ? "הודעה מ-My EyeControl" : "Message from My EyeControl");
    const body = encodeURIComponent(messageText);
    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
    handleClearAndNavigate();
  };

  const handleSendPush = async (messageText: string = text) => {
    if (!user) return;
    
    setIsSendingPush(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-push-alert', {
        body: {
          patientId: user.id,
          alertType: 'help',
          message: messageText,
        }
      });
      
      if (error) throw error;
      
      if (data?.sent > 0) {
        toast.success(isUIHebrew ? `נשלחה התראה ל-${data.sent} מכשירים` : `Sent to ${data.sent} devices`);
      } else {
        toast.info(isUIHebrew ? "אין מטפלים מחוברים" : "No caregivers connected");
      }
      
      handleBack();
    } catch (error) {
      toast.error(isUIHebrew ? "שגיאה בשליחה" : "Failed to send");
    } finally {
      setIsSendingPush(false);
    }
  };

  const handleSendToContacts = () => {
    const canEmail = contactConfig.hasEmail;
    const canPush = contactConfig.hasPush;
    
    if (canEmail && canPush) {
      setView("sendChoice");
    } else if (canEmail) {
      handleSendEmail();
    } else if (canPush) {
      handleSendPush();
    } else {
      sessionStorage.setItem("sendMessageText", text);
      handleClearAndNavigate();
      navigate("/contacts?action=send");
    }
  };

  const handleSpeak = async (textToSpeak: string = text, lang?: string) => {
    if (!textToSpeak.trim()) return;
    
    const language = lang || (detectLanguage(textToSpeak) === "he" ? "he" : "en");
    
    toast.success(isUIHebrew ? "מדבר..." : "Speaking...");
    
    await speak({
      text: textToSpeak,
      language,
      onError: () => {
        toast.error(isUIHebrew ? "שגיאה בהקראה" : "Speech error");
      },
    });
  };

  const handleTranslate = async (langCode: string) => {
    setTargetLang(langCode);
    setIsTranslating(true);
    
    try {
      const langNames: Record<string, string> = {
        en: "English",
        he: "Hebrew",
        ru: "Russian",
        ar: "Arabic",
        es: "Spanish",
      };
      
      const { data, error } = await supabase.functions.invoke('translate', {
        body: { 
          text,
          targetLanguage: langNames[langCode] || langCode
        }
      });
      
      if (error) throw error;
      
      setTranslatedText(data.translatedText || text);
      toast.success(isUIHebrew ? "תורגם!" : "Translated!");
    } catch (err) {
      setTranslatedText(text);
      toast.error(isUIHebrew ? "שגיאה בתרגום" : "Translation error");
    } finally {
      setIsTranslating(false);
    }
  };

  const handleSendToAIChat = (textToSend: string = text) => {
    sessionStorage.setItem("aiChatInitialMessage", textToSend);
    handleClearAndNavigate();
    navigate("/ai-chat");
  };

  const handleAddToDictionary = () => {
    if (!text.trim()) return;
    const entry = addToDictionary(text);
    toast.success(
      isUIHebrew ? "נוסף למילון!" : "Added to dictionary!",
      { 
        description: isUIHebrew 
          ? `ראשי תיבות: ${entry.acronym}` 
          : `Acronym: ${entry.acronym}` 
      }
    );
    handleBack();
  };

  const resetView = () => {
    setView("main");
    setTranslatedText("");
    setTargetLang("");
  };

  if (!text) {
    return (
      <MainLayout>
        <div className="flex-1 flex items-center justify-center p-8" dir={dir}>
          <div className="text-center space-y-4">
            <p className="text-xl text-muted-foreground">
              {isUIHebrew ? "לא נבחר טקסט" : "No text selected"}
            </p>
            <Button onClick={() => navigate("/")} data-scan-item>
              <ArrowLeft className="w-5 h-5 mr-2" />
              {isUIHebrew ? "חזרה" : "Back"}
            </Button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout onBack={handleBack}>
      <div className="flex-1 flex flex-col p-6 overflow-auto" dir={dir}>
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={handleBack} data-scan-item>
            <ArrowLeft className="w-6 h-6" />
          </Button>
          <h1 className="text-2xl font-bold text-foreground">
            {view === "main" 
              ? (isUIHebrew ? "פעולות" : "Actions")
              : view === "sendChoice"
              ? (isUIHebrew ? "שליחה למטפל" : "Send to Caregiver")
              : (isUIHebrew ? "תרגום" : "Translate")
            }
          </h1>
        </div>

        <div className="max-w-2xl mx-auto w-full space-y-6">
          {view === "main" && (
            <>
              {/* Preview text */}
              <div className="p-6 bg-muted rounded-2xl">
                <p className="text-xl text-foreground font-medium">{text}</p>
              </div>

              {/* Action buttons */}
              <div className="grid grid-cols-1 gap-3">
                <Button
                  variant="outline"
                  data-scan-item
                  className="justify-start gap-4 h-20 text-xl"
                  onClick={() => handleSpeak()}
                >
                  <Volume2 className="w-8 h-8 text-primary" />
                  {isUIHebrew ? "השמעה ברמקול" : "Speak"}
                </Button>

                <Button
                  variant="outline"
                  data-scan-item
                  className="justify-start gap-4 h-20 text-xl"
                  onClick={handleSendToContacts}
                >
                  <Users className="w-8 h-8 text-amber-500" />
                  {isUIHebrew ? "שליחה דרך צור קשר" : "Send via Contacts"}
                </Button>

                <Button
                  variant="outline"
                  data-scan-item
                  className="justify-start gap-4 h-20 text-xl"
                  onClick={() => handleSendToAIChat()}
                >
                  <Bot className="w-8 h-8 text-green-500" />
                  {isUIHebrew ? "שליחה לצ'אט AI" : "Send to AI Chat"}
                </Button>

                <Button
                  variant="outline"
                  data-scan-item
                  className="justify-start gap-4 h-20 text-xl"
                  onClick={() => setView("translate")}
                >
                  <Languages className="w-8 h-8 text-blue-500" />
                  {isUIHebrew ? "תרגום" : "Translate"}
                </Button>

                <Button
                  variant="outline"
                  data-scan-item
                  className="justify-start gap-4 h-20 text-xl"
                  onClick={handleAddToDictionary}
                >
                  <BookPlus className="w-8 h-8 text-purple-500" />
                  {isUIHebrew ? "הוספה להשלמת מילים" : "Add to Dictionary"}
                  <span className="text-sm text-muted-foreground mr-auto">
                    ({generateAcronym(text)})
                  </span>
                </Button>
              </div>
            </>
          )}

          {view === "sendChoice" && (
            <>
              <Button
                variant="ghost"
                size="lg"
                onClick={() => setView("main")}
                data-scan-item
                className="gap-2"
              >
                <ArrowLeft className="w-5 h-5" />
                {isUIHebrew ? "חזור" : "Back"}
              </Button>

              <div className="p-6 bg-muted rounded-2xl">
                <p className="text-xl text-foreground font-medium">{text}</p>
              </div>

              <p className="text-lg text-muted-foreground">
                {isUIHebrew ? "בחר אופן שליחה:" : "Choose send method:"}
              </p>

              <div className="grid grid-cols-1 gap-4">
                <Button
                  variant="outline"
                  data-scan-item
                  className="justify-start gap-4 h-24 text-2xl"
                  onClick={() => handleSendEmail()}
                  disabled={!contactConfig.hasEmail}
                >
                  <Mail className="w-10 h-10 text-primary" />
                  {isUIHebrew ? "אימייל" : "Email"}
                </Button>

                <Button
                  variant="outline"
                  data-scan-item
                  className="justify-start gap-4 h-24 text-2xl"
                  onClick={() => handleSendPush()}
                  disabled={!contactConfig.hasPush || isSendingPush}
                >
                  {isSendingPush ? (
                    <Loader2 className="w-10 h-10 animate-spin text-orange-500" />
                  ) : (
                    <Bell className="w-10 h-10 text-orange-500" />
                  )}
                  {isUIHebrew ? "התראת Push" : "Push Alert"}
                </Button>
              </div>
            </>
          )}

          {view === "translate" && (
            <>
              <Button
                variant="ghost"
                size="lg"
                onClick={resetView}
                data-scan-item
                className="gap-2"
              >
                <ArrowLeft className="w-5 h-5" />
                {isUIHebrew ? "חזור" : "Back"}
              </Button>

              {!translatedText && !isTranslating ? (
                <>
                  <p className="text-lg text-muted-foreground">
                    {isUIHebrew ? "בחר שפת יעד:" : "Select target language:"}
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    {targetLanguages.map((lang) => (
                      <Button
                        key={lang.code}
                        variant="outline"
                        onClick={() => handleTranslate(lang.code)}
                        data-scan-item
                        className="h-20 text-xl"
                      >
                        {isUIHebrew ? lang.nameHe : lang.name}
                      </Button>
                    ))}
                  </div>
                </>
              ) : isTranslating ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-12 h-12 animate-spin text-primary" />
                  <span className="mr-4 text-xl text-muted-foreground">
                    {isUIHebrew ? "מתרגם..." : "Translating..."}
                  </span>
                </div>
              ) : (
                <>
                  <div className="p-6 bg-muted rounded-2xl">
                    <p className="text-sm text-muted-foreground mb-2">
                      {isUIHebrew ? "מקור:" : "Original:"}
                    </p>
                    <p className="text-lg text-foreground">{text}</p>
                  </div>

                  <div className="p-6 bg-primary/10 rounded-2xl border-2 border-primary/20">
                    <p className="text-sm text-muted-foreground mb-2">
                      {isUIHebrew ? "תרגום:" : "Translation:"}
                    </p>
                    <p className="text-xl text-foreground font-medium">{translatedText}</p>
                  </div>

                  <div className="grid grid-cols-1 gap-3">
                    <Button
                      variant="outline"
                      data-scan-item
                      className="justify-start gap-4 h-16 text-lg"
                      onClick={() => handleSpeak(translatedText, targetLang)}
                    >
                      <Volume2 className="w-6 h-6 text-primary" />
                      {isUIHebrew ? "השמעה" : "Speak"}
                    </Button>

                    <Button
                      variant="outline"
                      data-scan-item
                      className="justify-start gap-4 h-16 text-lg"
                      onClick={() => {
                        sessionStorage.setItem("sendMessageText", translatedText);
                        handleClearAndNavigate();
                        navigate("/contacts?action=send");
                      }}
                    >
                      <Users className="w-6 h-6 text-amber-500" />
                      {isUIHebrew ? "שליחה דרך צור קשר" : "Send via Contacts"}
                    </Button>

                    <Button
                      variant="outline"
                      data-scan-item
                      className="justify-start gap-4 h-16 text-lg"
                      onClick={() => handleSendToAIChat(translatedText)}
                    >
                      <Bot className="w-6 h-6 text-green-500" />
                      {isUIHebrew ? "שליחה לצ'אט AI" : "Send to AI Chat"}
                    </Button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
